import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { LESFUNCTIES, BASIS_KEYS, isAlleenBeheer } from '../config/features';

// FeatureContext — bepaalt per-school welke features aan/uit staan.
//
// Resolutie-volgorde (zie plan):
//   1. Actieve "bekijk als school"-preview → features van díe school.
//   2. Anders build-vlag REACT_APP_ALL_FEATURES === 'true' (test-build) → alles aan.
//   3. Anders school.features uit de AuthContext-join.
// Default: ontbrekende key of NULL → feature AAN (geen regressie).

const FeatureContext = createContext();

const ALL_FEATURES_BUILD = process.env.REACT_APP_ALL_FEATURES === 'true';
const PREVIEW_KEY = 'tw-preview-school';

// Kernfunctie: is `key` aan gegeven een features-object?
export function resolveFeature(featuresObj, key) {
  if (BASIS_KEYS.includes(key)) return true; // basispakket: altijd aan (26-10-2026)
  if (!featuresObj) return true;          // NULL/geen config → alles aan
  return featuresObj[key] !== false;      // alleen expliciet false verbergt
}

// isAlleenBeheer(profiel) staat in config/features.js (zonder imports, dus
// testbaar); hier opnieuw aangeboden voor wie al FeatureContext importeert.
export { isAlleenBeheer };

export function FeatureProvider({ children }) {
  const { school, docentProfile } = useAuth();
  // De preview-switcher is admin-only, maar de localStorage-sleutel is
  // per-browser: een achtergebleven admin-preview lekte door naar een later
  // ingelogd docent-account (dat de switcher niet eens ziet en dus vastzat
  // in het geprevievde menu — gemeld 10-08: 'geen toegang tot Noordhoff').
  // Daarom telt de preview alleen zolang er daadwerkelijk een admin inlogt.
  const previewToegestaan = docentProfile?.rol === 'admin';

  // Preview-state: { id, naam, features } van de school die je nabootst.
  const [previewSchool, setPreviewSchoolState] = useState(() => {
    try {
      const raw = localStorage.getItem(PREVIEW_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const setPreviewSchool = useCallback((schoolObj) => {
    setPreviewSchoolState(schoolObj);
    try {
      if (schoolObj) localStorage.setItem(PREVIEW_KEY, JSON.stringify(schoolObj));
      else localStorage.removeItem(PREVIEW_KEY);
    } catch { /* localStorage kan geblokkeerd zijn */ }
  }, []);

  // Welk features-object is leidend?
  const effectiveFeatures = useMemo(() => {
    if (previewSchool && previewToegestaan) return previewSchool.features ?? {}; // 1. preview (alleen admin)
    if (ALL_FEATURES_BUILD) return null;                       // 2. test-build → alles aan
    // 14-09-2026: een TestWisely-admin ziet alles, ook wat zijn eigen school
    // uit heeft staan. Zonder dit kon de beheerder van het platform geen toets
    // aanmaken omdat zijn school (Lumion) toetsen uit heeft. Wil hij zien wat
    // een school ziet, dan gebruikt hij de preview hierboven.
    if (docentProfile?.rol === 'admin') return null;            // 3. admin → alles aan
    return school?.features ?? null;                           // 4. eigen school
  }, [previewSchool, previewToegestaan, school, docentProfile]);

  // Per docent (12-09-2026): de schoolbeheerder zet functies per docent uit
  // (docenten.functies, migratie 057). Geldt binnen het schoolplafond. Bij
  // een "bekijk als school"-preview telt het niet mee; in de alles-aan-build
  // (test.testwisely.nl) wél, want dat is een bewuste keuze van de beheerder
  // en niet een schoolvlag die de testbouw juist wil opheffen.
  const docentFuncties = ((previewSchool && previewToegestaan) || docentProfile?.rol === 'admin')
    ? null
    : (docentProfile?.functies ?? null);

  // Preview kan ook een rol nabootsen (bv. Lumion-admin vs Lumion-docent),
  // zodat rol-afhankelijke items zoals "Coaching · beheer" correct meelopen.
  const previewRole = previewToegestaan ? (previewSchool?.role ?? null) : null;

  // Alleen beheer: uit het eigen profiel, of nagebootst door de preview
  // ("Fiducie — beheerder zonder les"). Ook in de alles-aan-build: dit is
  // geen schoolvlag maar een eigenschap van het account.
  const alleenBeheer = previewSchool && previewToegestaan
    ? previewSchool.alleenBeheer === true
    : isAlleenBeheer(docentProfile);

  const isEnabled = useCallback(
    (key) => !(alleenBeheer && LESFUNCTIES.includes(key))
      && resolveFeature(effectiveFeatures, key)
      && resolveFeature(docentFuncties, key),
    [effectiveFeatures, docentFuncties, alleenBeheer]
  );

  const value = useMemo(() => ({
    isEnabled,
    alleenBeheer,
    previewSchool,
    previewRole,
    setPreviewSchool,
    allFeaturesBuild: ALL_FEATURES_BUILD,
  }), [isEnabled, alleenBeheer, previewSchool, previewRole, setPreviewSchool]);

  return <FeatureContext.Provider value={value}>{children}</FeatureContext.Provider>;
}

// Hook: useFeature('coaching') → boolean.
export function useFeature(key) {
  const ctx = useContext(FeatureContext);
  if (!ctx) return true; // geen provider (bv. leerling-context) → niets verbergen
  return ctx.isEnabled(key);
}

// Hook: is de ingelogde (of nagebootste) gebruiker een beheerder zonder lestaak?
export function useAlleenBeheer() {
  const ctx = useContext(FeatureContext);
  return ctx ? ctx.alleenBeheer === true : false;
}

// Volledige context (voor preview-switcher).
export function useFeatureContext() {
  return useContext(FeatureContext);
}
